import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Phone, Mail, MapPin, Clock, User, MessageCircle } from 'lucide-react';

const contactInfo = [
  {
    icon: User,
    title: 'Contact Person',
    value: 'Mukul Sangani',
    link: null,
  },
  {
    icon: Phone,
    title: 'Phone Numbers',
    value: '+91 98252 18789 | +91 79724 31220',
    link: 'tel:+919825218789',
  },
  {
    icon: Mail,
    title: 'Email',
    value: 'ascentin@gmail.com',
    link: 'mailto:ascentin@gmail.com',
  },
  {
    icon: MapPin,
    title: 'Address',
    value: 'Sheet No 42, Besides Suyog 2, Plot No 213, Butbhavani Mandir Road, Vejalpur, Ahmedabad – 380051',
    link: 'https://maps.google.com/?q=23.0089,72.5097',
  },
  {
    icon: Clock,
    title: 'Business Hours',
    value: 'Mon - Sat: 9:00 AM - 7:00 PM',
    link: null,
  },
];

const ContactSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent(
      "Hi ASCENT INTERNATIONAL! I'm interested in your signage services. Can you please provide more information?"
    );
    window.open(`https://wa.me/919825218789?text=${message}`, '_blank');
  };

  return (
    <section
      id="contact"
      className="py-24 md:py-32 bg-white relative overflow-hidden"
      data-testid="contact-section"
    >
      {/* Background elements */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-sky-50 rounded-full blur-3xl opacity-50"></div>
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-violet-50 rounded-full blur-3xl opacity-50"></div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 relative z-10">
        {/* Section Header */}
        <motion.div
          ref={ref}
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6 }}
        >
          <span className="text-sky-500 text-sm font-semibold uppercase tracking-[0.2em] mb-4 block font-['Manrope']">
            Get In Touch
          </span>
          <h2 className="section-heading mb-6">
            LET'S <span className="gradient-text">CONNECT</span>
          </h2>
          <p className="section-subheading mx-auto">
            Ready to transform your brand with premium signage? Contact us today for a free consultation and quote.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Info Cards */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -30 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="space-y-4"
          >
            {contactInfo.map((info, index) => {
              const Icon = info.icon;
              const Content = (
                <div className="contact-card flex items-start gap-4 group">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-sky-100 to-violet-100 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                    <Icon className="w-5 h-5 text-sky-500" />
                  </div>
                  <div>
                    <h4 className="text-slate-800 font-['Oswald'] font-bold mb-1">
                      {info.title}
                    </h4>
                    <p className="text-slate-500 font-['Manrope'] text-sm">
                      {info.value}
                    </p>
                  </div>
                </div>
              );

              return info.link ? (
                <a
                  key={index}
                  href={info.link}
                  target={info.title === 'Address' ? '_blank' : undefined}
                  rel={info.title === 'Address' ? 'noopener noreferrer' : undefined}
                  data-testid={`contact-${info.title.toLowerCase().replace(/\s/g, '-')}`}
                >
                  {Content}
                </a>
              ) : (
                <div key={index} data-testid={`contact-${info.title.toLowerCase().replace(/\s/g, '-')}`}>
                  {Content}
                </div>
              );
            })}

            {/* WhatsApp CTA Button */}
            <motion.button
              onClick={handleWhatsAppClick}
              className="w-full mt-6 flex items-center justify-center gap-3 bg-[#25D366] text-white font-bold uppercase tracking-wider px-8 py-4 rounded-xl hover:bg-[#1da851] transition-all duration-300 shadow-lg hover:shadow-xl"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              data-testid="whatsapp-cta-btn"
            >
              <MessageCircle className="w-5 h-5" />
              Chat on WhatsApp
            </motion.button>

            {/* Call Buttons */}
            <div className="grid grid-cols-2 gap-4 mt-4">
              <a
                href="tel:+919825218789"
                className="flex items-center justify-center gap-2 bg-slate-800 text-white font-bold uppercase tracking-wider px-4 py-3 rounded-xl hover:bg-slate-700 transition-all duration-300"
                data-testid="call-btn-1"
              >
                <Phone className="w-4 h-4" />
                Call: 98252 18789
              </a>
              <a
                href="tel:+917972431220"
                className="flex items-center justify-center gap-2 bg-slate-800 text-white font-bold uppercase tracking-wider px-4 py-3 rounded-xl hover:bg-slate-700 transition-all duration-300"
                data-testid="call-btn-2"
              >
                <Phone className="w-4 h-4" />
                Call: 79724 31220
              </a>
            </div>
          </motion.div>

          {/* Google Map */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: 30 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="rounded-2xl overflow-hidden shadow-elevated h-[400px] lg:h-full min-h-[400px]"
          >
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3672.3!2d72.5097!3d23.0089!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjPCsDAwJzMyLjAiTiA3MsKwMzAnMzUuMCJF!5e0!3m2!1sen!2sin!4v1700000000000!5m2!1sen!2sin"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="ASCENT INTERNATIONAL INC. Location - LED Sign Board Manufacturer Ahmedabad"
              data-testid="google-map"
            ></iframe>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
